import React, { useState, useEffect, useRef } from 'react';
import { getTeams, adminCreateTeam, adminUpdateTeam, adminDeleteTeam, adminCreatePlayer, adminUpdatePlayer, adminDeletePlayer, getPlayers, adminUploadPlayerPhoto, adminGetMembers } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Target, Camera, UserPlus, Shield } from 'lucide-react';
import api from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTeams = () => {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [editingPlayerDialogOpen, setEditingPlayerDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [assigningTeam, setAssigningTeam] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', description: '', league: '' });
  const [teamPhoto, setTeamPhoto] = useState(null);
  const [playerForm, setPlayerForm] = useState({ name: '', team_ids: [], function: '', bio: '' });
  const [playerPhoto, setPlayerPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [playerTeamFunctions, setPlayerTeamFunctions] = useState({});
  const teamFileRef = useRef(null);
  const playerFileRef = useRef(null);

  const fetchData = async () => {
    try {
      const [teamsRes, playersRes] = await Promise.all([getTeams(), getPlayers()]);
      setTeams(teamsRes.data);
      setPlayers(playersRes.data);
    } catch (e) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const handleSaveTeam = async () => {
    setSubmitting(true);
    try {
      let data = { ...teamForm };
      if (teamPhoto) {
        const fd = new FormData(); fd.append('file', teamPhoto);
        const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        data.photo_url = r.data.path;
      }
      if (editingTeam) {
        await adminUpdateTeam(editingTeam.team_id, data);
        toast.success('Mannschaft aktualisiert');
      } else {
        await adminCreateTeam(data);
        toast.success('Mannschaft erstellt');
      }
      setTeamDialogOpen(false); setEditingTeam(null); setTeamPhoto(null);
      setTeamForm({ name: '', description: '', league: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`"${team.name}" löschen?`)) return;
    try { await adminDeleteTeam(team.team_id); toast.success('Gelöscht'); fetchData(); }
    catch (e) { toast.error('Fehler'); }
  };

  const handleSavePlayer = async () => {
    setSubmitting(true);
    try {
      const data = {
        ...playerForm,
      };
      const res = await adminCreatePlayer(data);
      if (playerPhoto && res.data.player_id) {
        const fd = new FormData(); fd.append('file', playerPhoto);
        await adminUploadPlayerPhoto(res.data.player_id, fd);
      }
      toast.success('Spieler erstellt');
      setPlayerDialogOpen(false); setPlayerPhoto(null);
      setPlayerForm({ name: '', team_ids: [], function: '', bio: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSubmitting(false); }
  };

  const handleUpdatePlayer = async () => {
    if (!editingPlayer) return;
    setSubmitting(true);
    try {
      const data = {
        name: playerForm.name,
        function: playerForm.function,
        bio: playerForm.bio,
        team_ids: playerForm.team_ids,
      };
      await adminUpdatePlayer(editingPlayer.player_id, data);
      if (playerPhoto) {
        const fd = new FormData(); fd.append('file', playerPhoto);
        await adminUploadPlayerPhoto(editingPlayer.player_id, fd);
      }
      toast.success('Spieler aktualisiert');
      setEditingPlayerDialogOpen(false); setEditingPlayer(null); setPlayerPhoto(null);
      fetchData();
    } catch (e) { toast.error('Fehler'); }
    finally { setSubmitting(false); }
  };

  const handleDeletePlayer = async (p) => {
    if (!window.confirm(`"${p.name}" löschen?`)) return;
    try { await adminDeletePlayer(p.player_id); toast.success('Gelöscht'); fetchData(); }
    catch (e) { toast.error('Fehler'); }
  };

  const handleRemoveFromTeam = async (player, teamId) => {
    if (!window.confirm(`"${player.name}" aus dieser Mannschaft entfernen?`)) return;
    try {
      const currentTeamIds = player.team_ids || [];
      const currentTf = player.team_functions || {};
      const updatedTf = { ...currentTf };
      delete updatedTf[teamId];
      await adminUpdatePlayer(player.player_id, {
        team_ids: currentTeamIds.filter(id => id !== teamId),
        team_functions: updatedTf
      });
      toast.success('Spieler entfernt');
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  const openAssignDialog = (team) => {
    setAssigningTeam(team);
    const teamPlayerIds = players.filter(p => (p.team_ids || []).includes(team.team_id)).map(p => p.player_id);
    setSelectedPlayerIds(teamPlayerIds);
    // Init team functions from existing data
    const fns = {};
    players.forEach(p => {
      const tf = p.team_functions || {};
      if (tf[team.team_id]) fns[p.player_id] = tf[team.team_id];
    });
    setPlayerTeamFunctions(fns);
    setAssignDialogOpen(true);
  };

  const handleSaveAssignments = async () => {
    if (!assigningTeam) return;
    setSubmitting(true);
    try {
      for (const player of players) {
        const currentTeamIds = player.team_ids || [];
        const isSelected = selectedPlayerIds.includes(player.player_id);
        const isInTeam = currentTeamIds.includes(assigningTeam.team_id);
        const currentTf = player.team_functions || {};
        const newFn = playerTeamFunctions[player.player_id] || '';

        if (isSelected && !isInTeam) {
          const updatedTf = { ...currentTf, [assigningTeam.team_id]: newFn };
          await adminUpdatePlayer(player.player_id, { team_ids: [...currentTeamIds, assigningTeam.team_id], team_functions: updatedTf });
        } else if (!isSelected && isInTeam) {
          const updatedTf = { ...currentTf };
          delete updatedTf[assigningTeam.team_id];
          await adminUpdatePlayer(player.player_id, { team_ids: currentTeamIds.filter(id => id !== assigningTeam.team_id), team_functions: updatedTf });
        } else if (isSelected && isInTeam) {
          // Update function if changed
          if ((currentTf[assigningTeam.team_id] || '') !== newFn) {
            const updatedTf = { ...currentTf, [assigningTeam.team_id]: newFn };
            await adminUpdatePlayer(player.player_id, { team_functions: updatedTf });
          }
        }
      }
      toast.success('Zuordnung gespeichert');
      setAssignDialogOpen(false);
      fetchData();
    } catch (e) { toast.error('Fehler'); }
    finally { setSubmitting(false); }
  };

  const handlePlayerPhotoUpload = async (player) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const fd = new FormData(); fd.append('file', file);
        await adminUploadPlayerPhoto(player.player_id, fd);
        toast.success('Foto aktualisiert');
        fetchData();
      } catch (err) { toast.error('Upload fehlgeschlagen'); }
    };
    input.click();
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayerIds(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  const openEditPlayer = (p) => {
    setEditingPlayer(p);
    setPlayerForm({
      name: p.name,
      team_ids: p.team_ids || [],
      function: p.function || '',
      bio: p.bio || '',
    });
    setPlayerPhoto(null);
    setEditingPlayerDialogOpen(true);
  };

  return (
    <div data-testid="admin-teams-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">Mannschaften & Spieler</h1>
        <Button onClick={() => { setEditingTeam(null); setTeamForm({ name: '', description: '', league: '' }); setTeamPhoto(null); setTeamDialogOpen(true); }} className="btn-primary" data-testid="add-team-btn">
            <Plus className="w-4 h-4 mr-2" />Mannschaft
          </Button>
      </div>

      {loading ? (
        <div className="space-y-4">{[1, 2].map(i => <Card key={i} className="card-surface animate-pulse"><CardContent className="p-8"><div className="h-20 bg-white/10 rounded" /></CardContent></Card>)}</div>
      ) : teams.length > 0 ? (
        <div className="space-y-6">
          {teams.map(team => {
            const teamPlayers = players.filter(p => (p.team_ids || []).includes(team.team_id));
            return (
              <Card key={team.team_id} className="card-surface" data-testid={`admin-team-${team.team_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-14 h-14">
                        <AvatarImage src={getPhotoUrl(team.photo_url)} />
                        <AvatarFallback className="bg-primary/10"><Target className="w-7 h-7 text-primary" /></AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-xl font-heading font-bold uppercase">{team.name}</h3>
                        {team.league && <Badge variant="outline" className="border-accent text-accent">{team.league}</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-accent/20 text-accent hover:bg-accent/30 gap-1" onClick={() => openAssignDialog(team)} data-testid={`assign-players-${team.team_id}`}>
                        <UserPlus className="w-4 h-4" /> Zuordnen
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`edit-team-${team.team_id}`} onClick={() => { setEditingTeam(team); setTeamForm({ name: team.name, description: team.description || '', league: team.league || '' }); setTeamPhoto(null); setTeamDialogOpen(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-400" data-testid={`delete-team-${team.team_id}`} onClick={() => handleDeleteTeam(team)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {team.description && <p className="text-text-secondary text-sm mb-4">{team.description}</p>}
                  <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-text-secondary" /><span className="text-sm text-text-secondary">{teamPlayers.length} Spieler</span></div>
                  {teamPlayers.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {teamPlayers.map(p => {
                        const tf = p.team_functions || {};
                        const teamFn = tf[team.team_id] || p.function || '';
                        const isCapt = teamFn.toLowerCase() === 'kapitän';
                        return (
                          <div key={p.player_id} className={`card-elevated p-3 flex items-center justify-between ${isCapt ? 'ring-1 ring-yellow-500/30' : ''}`}>
                            <div className="flex items-center gap-3 cursor-pointer" onClick={() => openEditPlayer(p)}>
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={getPhotoUrl(p.photo_url)} />
                                <AvatarFallback className="bg-surface text-xs">{p.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center gap-1">
                                  <p className="font-medium text-sm">{p.name}</p>
                                  {isCapt && <Shield className="w-3.5 h-3.5 text-yellow-500" />}
                                </div>
                                <p className="text-xs text-text-secondary">{teamFn || '-'}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handlePlayerPhotoUpload(p)} title="Foto"><Camera className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEditPlayer(p)} title="Bearbeiten"><Edit className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400" onClick={() => handleRemoveFromTeam(p, team.team_id)} title="Aus Mannschaft entfernen"><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

        </div>
      ) : (
        <Card className="card-surface"><CardContent className="p-12 text-center"><Target className="w-16 h-16 text-text-secondary mx-auto mb-4" /><p className="text-text-secondary">Noch keine Mannschaften. Erstelle eine Mannschaft und füge Spieler hinzu.</p></CardContent></Card>
      )}

      {/* Team Dialog */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editingTeam ? 'Mannschaft bearbeiten' : 'Neue Mannschaft'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                {teamPhoto ? <img src={URL.createObjectURL(teamPhoto)} alt="" className="w-16 h-16 rounded-full object-cover" /> : editingTeam?.photo_url ? <img src={getPhotoUrl(editingTeam.photo_url)} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 bg-surface-elevated rounded-full flex items-center justify-center"><Target className="w-8 h-8 text-text-secondary" /></div>}
                <input ref={teamFileRef} type="file" accept="image/*" className="hidden" onChange={e => setTeamPhoto(e.target.files?.[0])} />
                <button onClick={() => teamFileRef.current?.click()} className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center"><Camera className="w-3 h-3 text-white" /></button>
              </div>
              <p className="text-sm text-text-secondary">Mannschafts-Logo</p>
            </div>
            <div className="space-y-2"><Label>Name *</Label><Input className="input-dark" value={teamForm.name} onChange={e => setTeamForm({ ...teamForm, name: e.target.value })} data-testid="team-name-input" /></div>
            <div className="space-y-2"><Label>Beschreibung</Label><Input className="input-dark" value={teamForm.description} onChange={e => setTeamForm({ ...teamForm, description: e.target.value })} /></div>
            <div className="space-y-2"><Label>Liga</Label><Input className="input-dark" value={teamForm.league} onChange={e => setTeamForm({ ...teamForm, league: e.target.value })} /></div>
            <Button onClick={handleSaveTeam} disabled={submitting} className="btn-primary w-full" data-testid="save-team-btn">{submitting ? 'Wird gespeichert...' : editingTeam ? 'Aktualisieren' : 'Erstellen'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Player Edit Dialog */}
      <Dialog open={editingPlayerDialogOpen} onOpenChange={setEditingPlayerDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle className="font-heading uppercase">Spieler bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                {playerPhoto ? <img src={URL.createObjectURL(playerPhoto)} alt="" className="w-16 h-16 rounded-full object-cover" /> : editingPlayer?.photo_url ? <img src={getPhotoUrl(editingPlayer.photo_url)} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 bg-surface-elevated rounded-full flex items-center justify-center"><Camera className="w-8 h-8 text-text-secondary" /></div>}
                <input type="file" accept="image/*" className="hidden" id="edit-player-photo" onChange={e => setPlayerPhoto(e.target.files?.[0])} />
                <button onClick={() => document.getElementById('edit-player-photo')?.click()} className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center"><Camera className="w-3 h-3 text-white" /></button>
              </div>
              <p className="text-sm text-text-secondary">Spieler-Foto</p>
            </div>
            <div className="space-y-2"><Label>Name *</Label><Input className="input-dark" value={playerForm.name} onChange={e => setPlayerForm({ ...playerForm, name: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Funktion</Label>
              <Select value={playerForm.function || 'keine'} onValueChange={v => setPlayerForm({ ...playerForm, function: v === 'keine' ? '' : v })}>
                <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  <SelectItem value="keine">Keine</SelectItem>
                  <SelectItem value="Kapitän">Kapitän</SelectItem>
                  <SelectItem value="Vize-Kapitän">Vize-Kapitän</SelectItem>
                  <SelectItem value="Trainer">Trainer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleUpdatePlayer} disabled={submitting} className="btn-primary w-full">{submitting ? 'Wird gespeichert...' : 'Aktualisieren'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Players Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-surface border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase">Spieler zuordnen: {assigningTeam?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {players.length > 0 ? players.map(p => {
              const isSelected = selectedPlayerIds.includes(p.player_id);
              return (
                <div key={p.player_id} className="p-3 card-elevated rounded-sm space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-white/5 rounded-sm">
                    <Checkbox checked={isSelected} onCheckedChange={() => togglePlayerSelection(p.player_id)} />
                    <Avatar className="w-8 h-8"><AvatarImage src={getPhotoUrl(p.photo_url)} /><AvatarFallback className="bg-surface text-xs">{p.name.split(' ').map(n => n[0]).join('')}</AvatarFallback></Avatar>
                    <p className="text-sm font-medium flex-1">{p.name}</p>
                  </label>
                  {isSelected && (
                    <Select value={playerTeamFunctions[p.player_id] || 'keine'} onValueChange={v => setPlayerTeamFunctions(prev => ({ ...prev, [p.player_id]: v === 'keine' ? '' : v }))}>
                      <SelectTrigger className="input-dark h-8 text-xs"><SelectValue placeholder="Funktion" /></SelectTrigger>
                      <SelectContent className="bg-surface border-white/10">
                        <SelectItem value="keine">Keine Funktion</SelectItem>
                        <SelectItem value="Kapitän">Kapitän</SelectItem>
                        <SelectItem value="Vize-Kapitän">Vize-Kapitän</SelectItem>
                        <SelectItem value="Trainer">Trainer</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            }) : (
              <p className="text-text-secondary text-center py-4">Keine Spieler vorhanden.</p>
            )}
          </div>
          <Button onClick={handleSaveAssignments} disabled={submitting} className="btn-primary w-full mt-4" data-testid="save-assignments-btn">
            {submitting ? 'Wird gespeichert...' : `${selectedPlayerIds.length} Spieler zuordnen`}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTeams;
